/**
 * App.tsx
 *
 * 애플리케이션 루트 컴포넌트
 * - 인증 체크 및 로그인 화면
 * - 인증 후 React Router 라우팅
 */

import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from './core/auth/useAuth'
import { isMockMode } from './core/config/env'
import { SecurityNotice } from './shared/components/ui/SecurityNotice'
import { SessionExpiredModal } from './core/auth/SessionExpiredModal'
import { useWorkspaceStore } from './core/workspace/workspaceStore'
import { WorkspaceOnboarding } from './core/workspace/components/WorkspaceOnboarding'
import { WorkspaceManagementPage } from './core/workspace/pages/WorkspaceManagementPage'
import { AppLayout } from './shared/components/layout/AppLayout'
import { TransactionListPage } from './domains/transaction/pages/TransactionListPage'
import { TransactionRegisterPage } from './domains/transaction/pages/TransactionRegisterPage'
import { TransactionBulkRegisterPage } from './domains/transaction/pages/TransactionBulkRegisterPage'
import { TransactionStatisticsPage } from './domains/transaction/pages/TransactionStatisticsPage'
import { BudgetStatusPage } from './domains/budget/pages/BudgetStatusPage'
import { SettlementPage } from './domains/settlement/pages/SettlementPage'
import { SettingsPage } from './pages/SettingsPage'
import { BudgetSettingsPage } from './domains/budget/pages/BudgetSettingsPage'
import { MonthlyBudgetOverridePage } from './domains/budget/pages/MonthlyBudgetOverridePage'
import { AppSettingsPage } from './pages/AppSettingsPage'
import { DeveloperSettingsPage } from './pages/DeveloperSettingsPage'
import { PaymentMethodsPage } from './domains/payment/pages/PaymentMethodsPage'
import { CategoriesPage } from './domains/category/pages/CategoriesPage'
import { MembersPage } from './domains/user/pages/MembersPage'

function App() {
  const { isAuthenticated, login, logout, status, error } = useAuth()
  const { workspaces } = useWorkspaceStore()
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false)

  /**
   * 세션 만료 이벤트 리스닝
   *
   * GoogleSheetsClient에서 401 에러 발생 시 'session-expired' 이벤트를 발생시키면
   * 이를 감지하여 세션 만료 모달을 표시합니다.
   */
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('⚠️ 세션이 만료되었습니다. 재로그인이 필요합니다.')
      setShowSessionExpiredModal(true)
    }

    window.addEventListener('session-expired', handleSessionExpired)

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired)
    }
  }, [])

  /**
   * 재로그인 핸들러
   *
   * 1. 기존 토큰 및 사용자 정보 삭제 (로그아웃)
   * 2. 세션 만료 모달 닫기
   * 3. Google 로그인 프로세스 시작
   */
  const handleRelogin = () => {
    logout()
    setShowSessionExpiredModal(false)
    // 로그아웃 후 자동으로 로그인 화면으로 이동되며, 사용자가 다시 로그인할 수 있음
  }

  // 토큰 검증 중 로딩 화면
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">로그인 상태 확인 중...</p>
        </div>
      </div>
    )
  }

  // 토큰 검증 에러 화면 (네트워크 오류)
  if (status === 'error' && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl max-w-md mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              연결 오류
            </h2>
            <p className="text-red-600 dark:text-red-400 mb-6">{error.message}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  // 로그인 화면 (인증되지 않은 경우)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              ourWallet
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              서버리스 공동 가계부 애플리케이션
            </p>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Google 계정으로 로그인하여 가계부를 시작하세요
            </p>
          </div>

          <button
            onClick={login}
            className="w-full bg-primary-600 dark:bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium"
          >
            Google 로그인
          </button>

          {/* 보안 안내 */}
          <div className="mt-6">
            <SecurityNotice />
          </div>

          {isMockMode() && (
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-xs text-yellow-800 dark:text-yellow-300">
                🔧 Mock 모드: 자동으로 로그인됩니다
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  // 워크스페이스 온보딩 (Mock 모드가 아니고, 워크스페이스가 없는 경우)
  if (!isMockMode() && workspaces.length === 0) {
    return <WorkspaceOnboarding />
  }

  // 인증된 경우: React Router로 페이지 라우팅
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppLayout />}>
            <Route index element={<TransactionListPage />} />
            <Route path="register" element={<TransactionRegisterPage />} />
            <Route path="register/bulk" element={<TransactionBulkRegisterPage />} />
            <Route path="status" element={<BudgetStatusPage />} />
            <Route path="statistics" element={<TransactionStatisticsPage />} />
            <Route path="settlement" element={<SettlementPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/budget" element={<BudgetSettingsPage />} />
            <Route path="settings/budget/monthly" element={<MonthlyBudgetOverridePage />} />
            <Route path="settings/payment-methods" element={<PaymentMethodsPage />} />
            <Route path="settings/categories" element={<CategoriesPage />} />
            <Route path="settings/members" element={<MembersPage />} />
            <Route path="settings/workspaces" element={<WorkspaceManagementPage />} />
            <Route path="settings/app" element={<AppSettingsPage />} />
            <Route path="settings/developer" element={<DeveloperSettingsPage />} />
          </Route>
        </Routes>
      </BrowserRouter>

      {/* 세션 만료 모달 */}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onRelogin={handleRelogin}
      />
    </>
  )
}

export default App
